import React from "react";
import { Link } from "react-router-dom";
import { Moon, Sun } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { SUPPORTED } from "@/i18n";

export function Logo({ to = "/", small }) {
  return (
    <Link to={to} className="flex items-center gap-2 group" data-testid="brand-logo">
      <img src="/logo.png" alt="TOPTALENT.IO" className="h-7 w-auto dark:bg-white dark:rounded-md dark:p-0.5 transition-transform duration-300 group-hover:scale-110" data-testid="brand-logo-img" />
      {!small && <span className="font-extrabold tracking-tight text-[15px]">TOPTALENT<span className="text-primary">.IO</span></span>}
    </Link>
  );
}

export function LangToggle() {
  const { locale, setLang, t } = useI18n();
  return (
    <select value={locale} onChange={(e) => setLang(e.target.value)} data-testid="language-toggle" aria-label={t("Interface language")}
      className="h-8 px-1.5 rounded-md bg-transparent text-xs font-semibold text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer">
      {Object.entries(SUPPORTED).map(([code, name]) => <option key={code} value={code} data-testid={`language-option-${code}`}>{name}</option>)}
    </select>
  );
}

export function ThemeToggle() {
  const { t } = useI18n();
  const [dark, setDark] = React.useState(document.documentElement.classList.contains("dark"));
  const toggle = () => {
    const next = !dark;
    document.documentElement.classList.toggle("dark", next);
    localStorage.setItem("tt_theme", next ? "dark" : "light");
    setDark(next);
  };
  return (
    <button onClick={toggle} data-testid="theme-toggle" aria-label={t("Toggle theme")}
      className="h-8 w-8 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
      {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
    </button>
  );
}

export function EmptyState({ icon: Icon, title, body, action, testid }) {
  return (
    <div className="card-surface border-dashed p-10 text-center" data-testid={testid || "empty-state"}>
      {Icon && <div className="mx-auto w-11 h-11 rounded-xl bg-accent flex items-center justify-center mb-3"><Icon className="w-5 h-5 text-accent-foreground" /></div>}
      <div className="font-semibold">{title}</div>
      {body && <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">{body}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function PageHeader({ eyebrow, title, desc, actions }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
      <div>
        {eyebrow && <div className="eyebrow mb-1">{eyebrow}</div>}
        <h1 className="text-2xl font-bold tracking-tight" data-testid="page-title">{title}</h1>
        {desc && <p className="text-sm text-muted-foreground mt-1 max-w-2xl">{desc}</p>}
      </div>
      {actions && <div className="flex gap-2">{actions}</div>}
    </div>
  );
}

export function Pill({ children, tone = "default", testid }) {
  const tones = {
    default: "bg-muted text-muted-foreground", indigo: "bg-accent text-accent-foreground",
    green: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300", amber: "bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
    red: "bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300",
  };
  return <span data-testid={testid} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${tones[tone]}`}>{children}</span>;
}

export const STATUS_TONE = { completed: "green", active: "green", running: "indigo", queued: "indigo", processing: "indigo", trialing: "amber",
  failed: "red", dead_letter: "red", blocked: "amber", cancelled: "default", pending: "amber", open: "amber", approved: "green", rejected: "red" };
